import { useState, useCallback, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

type Subcategory = {
  id: string;
  slug: string;
  title: {
    el: string;
    en: string;
  };
};

type ProductCarouselProps = {
  subcategories: Subcategory[];
  slug: string;
  language: 'el' | 'en';
  t: (el: string, en: string) => string;
  getProductImage: (subSlug: string) => string | undefined;
};

export const ProductCarousel = ({ subcategories, slug, language, t, getProductImage }: ProductCarouselProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: true,
    align: 'center',
    slidesToScroll: 1,
    skipSnaps: false,
    duration: 25,
    containScroll: 'trimSnaps'
  });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollTo = useCallback((index: number) => {
    if (emblaApi) emblaApi.scrollTo(index);
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') scrollPrev();
      if (e.key === 'ArrowRight') scrollNext();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect, scrollPrev, scrollNext]);

  return (
    <div className="relative max-w-6xl mx-auto">
      {/* Carousel */}
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-4 md:gap-6">
          {subcategories.map((sub, index) => {
            const productImage = getProductImage(sub.slug);
            const isActive = index === selectedIndex;
            
            return (
              <div 
                key={sub.id} 
                className={`flex-[0_0_85%] md:flex-[0_0_60%] lg:flex-[0_0_50%] min-w-0 transition-all duration-300 ${
                  isActive ? 'scale-100 opacity-100' : 'scale-90 opacity-60'
                }`}
              >
                <Card className="hover:shadow-2xl transition-all duration-300 group border-l-4 border-l-primary overflow-hidden h-full">
                  <CardContent className="p-0 h-full">
                    <Link to={`/industries/${slug}/${sub.slug}`} className="block h-full">
                      <div className="flex flex-col h-full">
                        {productImage && (
                          <div className="w-full h-64 md:h-80 relative overflow-hidden">
                            <img
                              src={productImage}
                              alt={sub.title[language]}
                              className={`w-full h-full object-cover group-hover:scale-110 transition-all duration-500 ${
                                isActive ? 'grayscale-0' : 'grayscale'
                              }`}
                              loading="lazy"
                            />
                          </div>
                        )}
                        <div className="flex-1 p-6 md:p-8 flex flex-col">
                          <h3 className="text-2xl md:text-3xl font-bold mb-4 group-hover:text-primary transition-colors flex-grow">
                            {sub.title[language]}
                          </h3>
                          <Button variant="ghost" className="group/btn p-0 min-h-6 h-auto hover:bg-transparent justify-start">
                            <span className="mr-2">{t('Περισσότερα', 'Learn More')}</span>
                            <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                          </Button>
                        </div>
                      </div>
                    </Link>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      </div>

      {/* Navigation Arrows */}
      <Button
        variant="outline"
        size="icon"
        className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-10 bg-background/80 hover:bg-background backdrop-blur-sm shadow-lg"
        onClick={scrollPrev}
        aria-label={t('Προηγούμενη κατηγορία', 'Previous category')}
      >
        <ChevronLeft className="h-6 w-6" />
      </Button>
      
      <Button
        variant="outline"
        size="icon"
        className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-10 bg-background/80 hover:bg-background backdrop-blur-sm shadow-lg"
        onClick={scrollNext}
        aria-label={t('Επόμενη κατηγορία', 'Next category')}
      >
        <ChevronRight className="h-6 w-6" />
      </Button>

      {/* Dots Navigation */}
      <div className="flex justify-center gap-1 mt-6">
        {subcategories.map((_, index) => (
          <button
            key={index}
            className="p-2 group"
            onClick={() => scrollTo(index)}
            aria-label={`${t('Μετάβαση στην κατηγορία', 'Go to category')} ${index + 1}`}
          >
            <span className={`block h-3 rounded-full transition-all duration-300 ${
              index === selectedIndex 
                ? 'bg-primary w-8' 
                : 'bg-muted-foreground/30 group-hover:bg-muted-foreground/50 w-3'
            }`} />
          </button>
        ))}
      </div>
    </div>
  );
};